import { useState, useEffect, useMemo, useReducer, lazy, memo } from 'react';
import { useAuth } from '../contexts/AuthContext'
import Footer from '../components/Footer'
import { supabase } from '../config/supabase'
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async'

// ควรย้าย initial state ไปไว้ข้างนอก component เพื่อป้องกันการสร้างใหม่ทุกครั้งที่ render
const initialSettings = {
  quality: '2', // High
  version: '6.1',
  styles: ['Realistic'],
  aspects: ['16:9']
};

const Generator = () => {
  const { user, signOut } = useAuth()
  const [keywords, setKeywords] = useState('');
  const [settings, setSettings] = useState(initialSettings);
  const [allPrompts, setAllPrompts] = useState([]);
  const [displayedPrompts, setDisplayedPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [desiredPrompts, setDesiredPrompts] = useState(10);
  const [customAspect, setCustomAspect] = useState({ width: '', height: '' });

  const [styles, setStyles] = useState({
    'Realistic': true,
    'Illustrations': false,
    'Anime': false,
    'Sculpture': false,
    'Digital Art': false,
    'Oil Painting': false,
    'Watercolor': false,
    'Sketch': false,
    'Comic': false,
    'Pattern': false
  });

  const [aspectRatios, setAspectRatios] = useState({
    '1:1': false,
    '16:9': true,
    '9:16': false,
    '4:3': false,
    '3:2': false
  });

  // จัดการการคลิกปุ่ม Style
  const handleStyleChange = (style) => {
    setSettings(prev => ({
      ...prev,
      styles: prev.styles.includes(style)
        ? prev.styles.filter(s => s !== style)
        : [...prev.styles, style]
    }));
  };

  // จัดการการคลิกปุ่ม Aspect Ratio
  const handleAspectChange = (aspect) => {
    setSettings(prev => ({
      ...prev,
      aspects: prev.aspects.includes(aspect)
        ? prev.aspects.filter(a => a !== aspect)
        : [...prev.aspects, aspect]
    }));
  };

  // จัดการการคลิกปุ่ม Quality
  const handleQualityChange = (quality) => {
    setSettings(prev => ({
      ...prev,
      quality
    }));
  };

  // สัดการการเช็คบ็อกซ์ของ Style
  const handleStyleToggle = (styleValue) => {
    setSettings(prev => {
      const newStyles = prev.styles.includes(styleValue)
        ? prev.styles.filter(s => s !== styleValue) // ลออกถ้ามีอยู่แล้ว
        : [...prev.styles, styleValue]; // เพิ่มเข้าไปถ้ังไมม
      
      return {
        ...prev,
        styles: newStyles
      };
    });
  };

  // จัดการการเช็คบ็อกซ์ของ Aspect
  const handleAspectToggle = (aspectValue) => {
    setSettings(prev => {
      const newAspects = prev.aspects.includes(aspectValue)
        ? prev.aspects.filter(a => a !== aspectValue) // ลบออกถ้ามีอยู่แล้ว
        : [...prev.aspects, aspectValue]; // เพิ่มเข้าไปถ้าไม่มี
      
      return {
        ...prev,
        aspects: newAspects
      };
    });
  };

  // สัดการการเปลี่ยนแปลงของ checkbox parameters
  const handleParameterChange = (param) => {
    setSettings({
      ...settings,
      parameters: {
        ...settings.parameters,
        [param]: !settings.parameters[param]
      }
    });
  };

  // จัดการการเปลี่ยนแปลงค่าของ parameters
  const handleParameterValueChange = (param, value) => {
    setSettings({
      ...settings,
      parameters: {
        ...settings.parameters,
        [`${param}Value`]: value
      }
    });
  };

  // สร้าง prompt
  const handleGeneratePrompt = async () => {
    setIsLoading(true);
    try {
      // ตรวจสอบ keywords
      if (!keywords?.trim()) {
        throw new Error('กรุณาใส่ keywords อย่างน้อย 1 รายการ');
      }

      const keywordsList = keywords
        .split('\n')
        .filter(k => k.trim());

      const prompts = [];
      const selectedStyles = settings?.styles?.length > 0 ? settings.styles : [''];
      const selectedAspects = settings?.aspects?.length > 0 ? settings.aspects : [''];
      
      // สร้าง prompts
      keywordsList.forEach(keyword => {
        selectedStyles.forEach(style => {
          selectedAspects.forEach(aspect => {
            let basePrompt = '';
            
            // เพิ่ม keyword และ style
            basePrompt += keyword.trim();
            if (style) {
              if (style === 'Realistic') {
                basePrompt += ', Realistic Photo';
              } else {
                basePrompt += `, ${style}`;
              }
            }

            // เพิ่ม parameters ที่เหลือ
            if (aspect) basePrompt += ` --ar ${aspect}`;
            
            if (settings.version === 'niji') {
              basePrompt += ' --niji';
            } else {
              basePrompt += ` --v ${settings.version}`;
            }
            
            basePrompt += ` --q ${settings.quality}`;

            prompts.push(basePrompt);
          });
        });
      });

      // สุ่มเลือก prompts ตามจำนวนที่ต้องการ
      let selectedPrompts = prompts;
      if (desiredPrompts && desiredPrompts < prompts.length) {
        selectedPrompts = [];
        const tempPrompts = [...prompts];
        for (let i = 0; i < desiredPrompts; i++) {
          const randomIndex = Math.floor(Math.random() * tempPrompts.length);
          selectedPrompts.push(tempPrompts[randomIndex]);
          tempPrompts.splice(randomIndex, 1);
        }
      }

      setAllPrompts(selectedPrompts);
      setDisplayedPrompts(selectedPrompts.slice(0, DISPLAY_LIMIT));

    } catch (error) {
      console.error('Error:', error);
      setNotification({
        show: true,
        message: error.message,
        type: 'error'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // ดาวน์โหลด prompts
  const downloadPrompts = async () => {
    try {
      const fileName = `prompts.txt`;
      const fileContent = allPrompts.join('\n');
      
      // สร้าง Blob และ URL
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      
      // สร้าง link และคลิกเพื่อดาวน์โหลด
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({
        show: true,
        message: 'ดาวน์โหลดไฟล์สำเร็จ',
        type: 'success'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);

    } catch (error) {
      console.error('Error:', error);
      setNotification({
        show: true,
        message: 'เกิดข้อผิดพลาดในการดาวน์โหลด: ' + error.message,
        type: 'error'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    }
  };

  // แคชฟังก์ชัน copyToClipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotification({
        show: true,
        message: 'คัลอกสำเร็จ!',
        type: 'success'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    } catch (err) {
      setNotification({
        show: true,
        message: 'ไม่สามารถคัดลอกได้: ' + err,
        type: 'error'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    }
  };

  // แคชฟังก์ชัน copyAllPrompts
  const copyAllPrompts = () => {
    const promptsText = allPrompts.join('\n');
    navigator.clipboard.writeText(promptsText);
    setNotification({
      show: true,
      message: 'คัดลอกทั้งหมดแล้ว',
      type: 'success'
    });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // เพิ่มฟังก์ชันนบจำนวน keywords
  const countKeywords = () => {
    return keywords
      .split('\n')
      .filter(keyword => keyword.trim() !== '')
      .length;
  };

  // เพิ่มฟังก์ชันคำนวณจำนวน prompts ที่จได้
  const calculateTotalPrompts = () => {
    const keywordsCount = keywords
      .split('\n')
      .filter(keyword => keyword.trim() !== '')
      .length;
    
    const stylesCount = settings?.styles?.length || 1;
    const aspectsCount = settings?.aspects?.length || 1;
    
    return keywordsCount * stylesCount * aspectsCount;
  };

  // เปลี่ยนค่า DISPLAY_LIMIT เป็น 10
  const DISPLAY_LIMIT = 10;

  // เพิ่มฟังก์ชันรีเซ็ต
  const handleReset = () => {
    localStorage.removeItem('promptSettings');
    setSettings(initialSettings);
  };

  // ปรับ array ของ styles
  const styleOptions = [
    { value: 'Realistic', label: 'Realistic' },
    { value: 'Illustrations', label: 'Illustrations' },
    { value: 'Watercolor', label: 'Watercolor' },
    { value: 'Anime', label: 'Anime' },
    { value: 'Digital Art', label: 'Digital Art' },
    { value: 'Oil Painting', label: 'Oil Painting' }
  ];

  // เพิ่มฟังก์ชันสัดการ custom aspect ratio
  const handleCustomAspect = () => {
    if (!customAspect.width || !customAspect.height) return;
    const aspectValue = `${customAspect.width}:${customAspect.height}`;
    handleAspectChange(aspectValue);
    setCustomAspect({ width: '', height: '' }); // รีเซ็ตค่าหลังจากเพิ่ม
  };

  // แ้ไขฟังก์ชัน handleSaveSettings
  const handleSaveSettings = () => {
    try {
      const settingsToSave = {
        ...settings
      };
      localStorage.setItem('promptSettings', JSON.stringify(settingsToSave));
      alert('บันทึกการตั้ง���่าเรียบร้อย');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('เกิดข้อผิดพ��าดในการบันทึกการตั้งค่า');
    }
  };

  // แก้ไข useEffect สำหรับโหลดการต้งค่า
  useEffect(() => {
    const loadSettings = () => {
      const savedSettings = localStorage.getItem('promptSettings');
      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          if (parsedSettings) {
            // ตั้งค่าเริ่มต้นสำหรับค่าที่อาจเป็น undefined
            const defaultSettings = {
              styles: [], // เปลี่ยนเป็น array ว่าง
              aspects: [],
              parameters: [],
              quality: initialSettings.quality || '2',
              version: initialSettings.version || '6.1',
              personalization: '',
              additional: ''
            };

            // รวมค่าที่บันทึกไว้กับค่าเริ่มต้น
            const mergedSettings = {
              ...defaultSettings,
              ...parsedSettings,
              // รับประกันว่า styles จะเป็น array
              styles: Array.isArray(parsedSettings.styles) ? parsedSettings.styles : [],
              aspects: Array.isArray(parsedSettings.aspects) ? parsedSettings.aspects : [],
              parameters: Array.isArray(parsedSettings.parameters) ? parsedSettings.parameters : []
            };

            setSettings(mergedSettings);
          }
        } catch (error) {
          console.error('Error loading settings:', error);
          // ถ้าเกิด error ให้ใช้ค่าเริ่มต้น
          setSettings({
            ...initialSettings,
            styles: [],
            aspects: [],
            parameters: []
          });
        }
      }
    };

    // โหลดการตั้งค่าหลังจากตรวจสอบ auth สำเร็จ
    loadSettings();
  }, []);

  // ใช้ useMemo สำหรับการคำนวณที่ซับซ้อน
  const calculatedPrompts = useMemo(() => {
    const keywordsCount = keywords
      .split('\n')
      .filter(keyword => keyword.trim() !== '')
      .length;
    
    const stylesCount = settings?.styles?.length || 1;
    const aspectsCount = settings?.aspects?.length || 1;
    
    return keywordsCount * stylesCount * aspectsCount;
  }, [keywords, settings?.styles?.length, settings?.aspects?.length]);

  // แยกฟังก์ชัน showNotification ออกมา
  const showNotification = (message, type = 'info') => {
    setNotification({
      show: true,
      message,
      type
    });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // แยก API calls ออกเป็น service
  const updatePromptCount = async (userId, newCount) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ prompt_count: newCount })
        .eq('id', userId);
      
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating prompt count:', error);
      return false;
    }
  };

  // แยกฟังก์ชันสำหรับการอัพเดทจำนวนครั้ง
  const updateDailyCount = async (currentCount) => {
    const today = new Date().toISOString().split('T')[0];
    
    const { error } = await supabase
      .from('daily_counts')
      .upsert({
        user_id: user.id,
        created_date: today,
        count: currentCount + 1
      }, {
        onConflict: 'user_id,created_date'
      });

    if (error) {
      console.error('Error updating count:', error);
      return false;
    }

    return true;
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Prompt Generator</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto p-6">
          
          {/* Keywords Section */}
          <div className="mb-8 bg-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-red-300">Keywords</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleStyleChange('Realistic')}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                    settings.styles.includes('Realistic')
                      ? 'bg-purple-500/20 border-purple-500 text-purple-400 shadow-lg shadow-purple-500/20'
                      : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-purple-500/50'
                  }`}
                >
                  Realistic
                </button>
                <button
                  onClick={() => handleStyleChange('Illustrations')}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                    settings.styles.includes('Illustrations')
                      ? 'bg-purple-500/20 border-purple-500 text-purple-400 shadow-lg shadow-purple-500/20'
                      : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-purple-500/50'
                  }`}
                >
                  Illustrations
                </button>
                <a 
                  href="https://discord.com/invite/MxgKZkydXJ" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium transition-colors"
                >
                  คู่มือการใช้งาน
                </a>
              </div>
            </div>

            {/* Keywords Input */}
            <div>
              <label className="block mb-2 font-medium text-gray-400">พิมพ์ Keywords (แต่ละบรรทัดคือ 1 keyword)</label>
              <textarea
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="พิมพ์ keywords แต่ละบรรทัด"
                className="w-full p-4 bg-black/40 border border-gray-700 rounded-lg text-gray-300 h-40 focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          {/* แสดงผำนวนทั้งหมด */}
          <div className="mb-8 bg-gradient-to-br from-purple-900/50 to-blue-900/50 rounded-lg p-6 border border-purple-700/50">
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center gap-12 flex-wrap">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Keywords</p>
                  <p className="text-2xl font-bold text-purple-400">{countKeywords()}</p>
                </div>
                <div className="h-12 w-px bg-gray-700"></div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Prompt</p>
                  <p className="text-2xl font-bold text-blue-400">{calculateTotalPrompts()}</p>
                </div>
                <div className="h-12 w-px bg-gray-700"></div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">รูป</p>
                  <p className="text-2xl font-bold text-green-400">{calculateTotalPrompts() * 4}</p>
                </div>
                <div className="h-12 w-px bg-gray-700"></div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">จำนวน Prompts ที่ต้องการ (สุ่มจากทั้งหมด)</p>
                  <input
                    type="number"
                    value={desiredPrompts}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value > calculateTotalPrompts()) {
                        setDesiredPrompts(calculateTotalPrompts());
                      } else {
                        setDesiredPrompts(value);
                      }
                    }}
                    min="1"
                    max={calculateTotalPrompts()}
                    placeholder="จำนวน"
                    className="w-24 px-3 py-1 bg-black/40 border border-gray-700 rounded text-center text-blue-400 font-bold"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ���ุ่มสร้าง Prompt */}
          <button 
            onClick={handleGeneratePrompt}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-semibold text-lg shadow-xl hover:shadow-purple-500/20 hover:scale-[1.02] backdrop-blur"
            disabled={isLoading}
          >
            {isLoading ? 'Generating...' : 'สร้าง Prompt'}
          </button>

          {/* แสดงผลลัพธ์ */}
          {displayedPrompts.length > 0 && (
            <div className="mt-8 bg-gray-900 rounded-lg p-6 border border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-200">Prompts ที่สร้าง</h2>
                <div className="flex items-center space-x-3">
                  <div className="text-sm space-x-2">
                    <span className="text-gray-400">ท้งหมด:</span>
                    <span className="text-green-300 font-medium">{allPrompts.length}</span>
                    <span className="text-gray-400">prompts</span>
                  </div>
                  {/* ปุ่มคัดลอกและดาวน์โหลด */}
                  <button
                    onClick={copyAllPrompts}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    <span>คัดลอกทั้งหมด</span>
                  </button>
                  <button
                    onClick={downloadPrompts}
                    className={`px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 013 3h10a3 3 0 013-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>ดาวน์โหลด .txt</span>
                  </button>
                </div>
              </div>
              
              {/* แสดงรายการ Prompts */}
              <div className="space-y-3">
                {displayedPrompts.slice(0, 1).map((prompt, index) => ( // แสดง���ค่ 1 อัน
                  <div key={index} className="group relative p-4 bg-black/40 rounded-lg hover:bg-black/60 transition-colors">
                    <p className="text-gray-300 font-mono break-all pr-10">{prompt}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(prompt);
                        alert('คัดลอกแล้ว');
                      }}
                      className="absolute right-2 top-2 p-2 bg-gray-800 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-700"
                      title="Copy to clipboard"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Style Section */}
          <div className="mb-8 bg-gray-900/90 rounded-lg p-6 border border-gray-800">
            {/* หัวข้อ Style */}
            <div className="p-4 bg-black/40 border-b border-purple-900/20 rounded-t-lg">
              <h2 className="text-xl font-semibold text-purple-300 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                Style (รูปแบบภาพ)
              </h2>
              <p className="text-sm text-gray-400 mt-2">เลือกรูปแบบของภาพที่ต้องการ สามารถเลือกได้หลายรูปแบบ เพื่อให้ได้ภาพที่หลากหลาย</p>
              <ul className="text-sm text-gray-500 mt-2 list-disc list-inside">
                <li>Realistic - ภาพถ่ายสมจริง เหมาะสำหรับสินค้า หรือภาพบุคคล</li>
                <li>Illustrations - ภาพวาดประกอบ เหมาะสำหรับงานออกแบบ หรือโลโก้</li>
                <li>Watercolor - ภาพวาดสีน้ำ สร้างค��ามรู้สึกอ่อนโยน และมีศิลปะ</li>
                <li>Digital Art - ภาพดิจิตอลอาร์ต เหมาะกับงานกราฟิกสมัยใหม่</li>
                <li>Oil Painting - ภาพวาดสีน้ำมัน ให้ความรู้สึกคลาสสิก</li>
              </ul>
            </div>

            {/* Style Grid */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
              {Object.entries(styles).map(([style, isSelected]) => (
                <button
                  key={style}
                  onClick={() => handleStyleChange(style)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                    isSelected
                      ? 'bg-purple-500/20 border-purple-500 text-purple-400 shadow-lg shadow-purple-500/20'
                      : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-purple-500/50'
                  }`}
                >
                  {style}
                </button>
              ))}
            </div>
          </div>

          {/* Aspect Ratio Section */}
          <div className="mb-8 bg-gray-900/90 rounded-lg p-6 border border-gray-800">
            {/* หัวข้อ Aspect Ratio */}
            <div className="p-4 bg-black/40 border-b border-blue-900/20 rounded-t-lg">
              <h2 className="text-xl font-semibold text-blue-300 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                Aspect Ratio (สัดส่วนภาพ)
              </h2>
              <p className="text-sm text-gray-400 mt-2">เลือกอัตราส่วนของภาพที่ต้องการ แต่ละขนาดเหมาะกับการใช้งานที่แตกต่างกัน</p>
              <ul className="text-sm text-gray-500 mt-2 list-disc list-inside">
                <li>1:1 (Square) - สี่เหลี่ยมจัตุรัส เหมาะสำหรับ Instagram</li>
                <li>16:9 (Landscape) - แนวนอน เหมาะสำหรับวิดีโอ YouTube</li>
                <li>9:16 (Portrait) - แนวตั้ง เหมาะสำหรับ Story, TikTok</li>
                <li>4:3 (Standard) - อัตราส่วนมาตรฐาน เหมาะสำหรับงานพรีเซนต์</li>
                <li>3:2 (Classic) - อัตราส่วนคลาสสิก เหมาะสำหรับงานพิมพ์</li>
              </ul>
            </div>
            
            {/* Aspect Ratio Grid */}
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mt-3">
              {Object.entries(aspectRatios).map(([ratio, isSelected]) => (
                <button
                  key={ratio}
                  onClick={() => handleAspectChange(ratio)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                    isSelected
                      ? 'bg-purple-500/20 border-purple-500 text-purple-400 shadow-lg shadow-purple-500/20'
                      : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-purple-500/50'
                  }`}
                >
                  {ratio}
                </button>
              ))}
            </div>
          </div>

          {/* Settings Section */}
          <div className="mb-8 bg-gray-900/90 rounded-lg p-6 border border-gray-800">
            {/* หัวข้อ Settings */}
            <div className="p-4 bg-black/40 border-b border-green-900/20 rounded-t-lg">
              <h2 className="text-xl font-semibold text-green-300 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings (การตั้งค่า)
              </h2>
              <p className="text-sm text-gray-400 mt-2">ปรับแต่งคุณภาพและเวอร์ชันของ AI ที่ใช้สร้างภาพ</p>
              <ul className="text-sm text-gray-500 mt-2 list-disc list-inside">
                <li>Quality:
                  <ul className="ml-6">
                    <li>High (2) - คุณภาพสูง ใช้เวลาสร้างนานกว่า</li>
                    <li>Medium (1) - คุณภาพปานกลาง เหมาะสำหรับงานทั่วไป</li>
                    <li>Low (.5) - คุณภาพต่ำ สร้างเร็ว เหมาะสำหรับทดลอง</li>
                  </ul>
                </li>
                <li>Version:
                  <ul className="ml-6">
                    <li>6.1 - เวอร์ชันล่าสุด รองรับฟีเจอร์ใหม่ทั้งหมด</li>
                    <li>Niji - โหมดพิเศษสำหรับภาพสไตล์อนิเมะ</li>
                  </ul>
                </li>
              </ul>
            </div>

            {/* เพิ่มส่วนตั้งค่า Quality และ Version */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* Quality Settings */}
              <div className="bg-black/40 p-4 rounded-lg border border-green-500/30">
                <label className="block text-sm text-green-400/70 mb-2">Quality</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: '2', label: 'High' },
                    { value: '1', label: 'Medium' },
                    { value: '0.5', label: 'Low' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => handleQualityChange(value)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                        settings.quality === value
                          ? 'bg-green-500/20 border-green-500 text-green-400 shadow-lg shadow-green-500/20'
                          : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-green-500/50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Version Settings */}
              <div className="bg-black/40 p-4 rounded-lg border border-green-500/30">
                <label className="block text-sm text-green-400/70 mb-2">Version</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: '6.1', label: 'V6.1' },
                    { value: 'niji', label: 'Niji' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setSettings(prev => ({ ...prev, version: value }))}
                      className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                        settings.version === value
                          ? 'bg-green-500/20 border-green-500 text-green-400 shadow-lg shadow-green-500/20'
                          : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-green-500/50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section แสดงรายละเอียดสำาชิก */}
          <div className="mt-12">
            <div className="p-6 bg-gradient-to-br from-yellow-500/5 to-green-500/5 rounded-lg border border-yellow-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-yellow-400">ห้ามพลาด! สมาชิกตลอดชีพ</h3>
                  <p className="text-sm text-gray-400 mt-1">จ่ายครั้งเดียว ใช้งานได้ตลอดชีพ</p>
                </div>

                <div className="text-center">
                  <button 
                    className="inline-block px-6 py-3 bg-gray-500 text-white rounded-lg cursor-not-allowed"
                    disabled
                  >
                    หมด
                  </button>
                  <p className="mt-2 text-xs text-red-400 animate-pulse">
                   ตอนนี้ยังไม่เปิดรับเพิ่ม
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* เพิ่มส่วนแสดงการแจ้งเตือน */}
      {notification.show && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg transition-all transform ${
          notification.type === 'success' ? 'bg-green-500/90' :
          notification.type === 'error' ? 'bg-red-500/90' :
          'bg-blue-500/90'
        }`}>
          <p className="text-white">{notification.message}</p>
        </div>
      )}
    </div>
  );
};

Generator.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    // ... other user properties
  }),
  // ... other props
};

export default Generator;